import { Component, OnInit, OnDestroy, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { TranslocoModule } from '@jsverse/transloco';
import { CurrentUserService } from '../../services/current-user.service';

import { SidebarService } from './sidebar.service';
import {
  SidebarItemComponent,
  SidebarMenuItem,
} from './sidebar-item/sidebar-item.component';
import { SidebarFooterComponent } from './sidebar-footer/sidebar-footer.component';
import { DiscoveryIndicatorComponent } from '../discovery-indicator/discovery-indicator.component';
import { CompanyContextSelectorComponent } from '../company-context-selector/company-context-selector.component';
import { CompanyContextService } from '../../services/company-context.service';
import { PackLimitationsService } from '../../services/pack-limitations.service';
import { ObjectivesCompletionService } from '../../services/objectives-completion.service';

// Tablet screen breakpoints (768px - 1024px)
// 768px est la borne basse où la sidebar remplace la mobile-toolbar (voir le media query
// du SCSS) : c'est aussi la largeur d'un iPad 9,7"/10,2" en portrait, où une sidebar
// dépliée de 372px mangerait la moitié de l'écran.
const TABLET_MIN_WIDTH = 768;
const TABLET_MAX_WIDTH = 1024;

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LucideAngularModule,
    TooltipModule,
    RippleModule,
    TranslocoModule,
    SidebarItemComponent,
    SidebarFooterComponent,
    DiscoveryIndicatorComponent,
    CompanyContextSelectorComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private sidebarService = inject(SidebarService);
  private currentUserService = inject(CurrentUserService);
  private companyContextService = inject(CompanyContextService);
  private packLimitationService = inject(PackLimitationsService);
  private objectivesCompletionService = inject(ObjectivesCompletionService);

  isCollapsed = false;
  private resizeListener?: () => void;

  // Utilisation des signals du service partagé
  currentUser = this.currentUserService.currentUser;
  isSuperAdmin = this.currentUserService.isSuperAdmin;
  isLoading = this.currentUserService.isLoading;

  baseSecondaryItems: SidebarMenuItem[] = [
    // {
    //   label: 'navigation.settings',
    //   icon: 'settings',
    //   route: '/home/account',
    //   testId: 'menu.settings',
    // },
    // {
    //   label: 'navigation.resources',
    //   icon: 'book',
    //   route: '/home/resources',
    //   testId: 'menu.resources',
    // },
    {
      label: 'navigation.help',
      icon: 'help-circle',
      route: '/home/help',
      testId: 'menu.help',
    },
  ];

  // Computed signal pour les éléments secondaires avec super-admin
  secondaryItems = computed(() => {
    const items = [...this.baseSecondaryItems];
    if (this.isSuperAdmin()) {
      items.unshift({
        label: 'navigation.admin',
        icon: 'shield',
        route: '/admin',
        testId: 'menu.admin',
        onClick: () => this.companyContextService.clearActingCompany(),
      });
    }
    return items;
  });

  menuItems = computed<SidebarMenuItem[]>(() => {
    const items: SidebarMenuItem[] = [
      // {
      //   label: 'navigation.dashboard',
      //   icon: 'home',
      //   route: '/home/dashboard-temp',
      //   testId: 'menu.dashboard',
      // },
      {
        label: 'navigation.trips',
        icon: 'image',
        route: '/home/travel-board',
        testId: 'menu.travelBoard',
      },
      {
        label: 'navigation.clients',
        icon: 'users',
        route: '/home/clients',
        testId: 'menu.clients',
        isLocked: !this.packLimitationService.canFilterClient(),
      },
      // {
      //   label: 'navigation.calculators',
      //   icon: 'calculator',
      //   route: '/home/calculators',
      //   testId: 'menu.calculators',
      // },
      {
        label: 'navigation.objectives',
        icon: 'target',
        route: '/home/objectives',
        testId: 'menu.objectives',
        warning: this.objectivesCompletionService.needsAttention(),
        warningLabel: 'navigation.objectivesIncomplete',
      },
      {
        label: 'navigation.factors',
        icon: 'calculator',
        route: '/home/factors',
        testId: 'menu.factors',
      },
    ];

    if (this.isSuperAdmin()) {
      items.push({
        label: 'navigation.contributions',
        icon: 'leaf',
        route: '/home/contributions',
        testId: 'menu.contributions',
      });
    }

    return items;
  });

  ngOnInit(): void {
    this.sidebarService.isCollapsed.subscribe((collapsed) => {
      this.isCollapsed = collapsed;
    });

    // Load user information via shared service
    this.currentUserService.loadCurrentUser().subscribe();

    // Force sidebar collapse on tablet screens
    this.handleScreenResize();

    // Listen to window resize events
    if (typeof window !== 'undefined') {
      this.resizeListener = () => this.handleScreenResize();
      window.addEventListener('resize', this.resizeListener);
    }
  }

  ngOnDestroy(): void {
    // Clean up resize listener
    if (typeof window !== 'undefined' && this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  /**
   * Check if current screen size is tablet (769px - 1024px)
   */
  private isTabletScreen(): boolean {
    if (typeof window === 'undefined') return false;
    const width = window.innerWidth;
    return width >= TABLET_MIN_WIDTH && width <= TABLET_MAX_WIDTH;
  }

  /**
   * Handle screen resize to force sidebar collapse on tablets
   */
  private handleScreenResize(): void {
    if (this.isTabletScreen()) {
      // Force collapse on tablet
      this.sidebarService.collapse();
    }
  }

  onToggle(): void {
    // Prevent any sidebar toggle on tablet screens (sidebar must stay collapsed)
    if (this.isTabletScreen()) {
      return;
    }

    this.sidebarService.toggle();
  }
}
